import { dehydrate, hydrate, VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia } from 'pinia';
import { ViteSSG } from 'vite-ssg';

import App from '#/App.vue';
import { initAnalytics } from '#/lib/analytics';
import { createQueryClient, queryClient } from '#/lib/queryClient';
import { registerAuthGuard, routes, scrollBehavior } from '#/router';
import { registerAnalytics } from '#/router/analytics';
import { useCommerceStore } from '#/stores/commerce';

import '#/assets/tailwind.css';

/**
 * 진입점 — vite-ssg(SSG) 로 공개 페이지를 빌드 타임 프리렌더한다.
 *
 * 순수 CSR 이던 진입점을 ViteSSG 로 전환: 크롤러·생성형 AI 봇이 JS 실행 없이도 본문·메타·JSON-LD 를
 * 읽도록 공개 라우트(가이드·홈·약관 등)의 완성 HTML 을 빌드 시 생성한다(SEO/GEO 토대).
 *
 * ViteSSG 가 자동 처리하므로 여기서 직접 하지 않는 것:
 *  - 라우터 생성·등록: routes/scrollBehavior 만 넘기면 빌드=memory·브라우저=history 로 ViteSSG 가 만든다.
 *  - head(@unhead/vue) 등록: ViteSSG 가 createHead 를 자동 use 한다(중복 등록 방지). useSeoHead/useHead 는 그대로 동작.
 *  - app.mount: ViteSSG 가 수화(hydration)/마운트를 담당.
 *
 * setup 콜백(fn)에서는 앱 스코프 플러그인(Pinia·VueQuery)만 등록한다. 문서 타이틀은 여기서 건드리지 않는다 —
 * 라우트 meta.title 기반 `useRouteHead`(App.vue) 가 SSoT. 과거 여기서 `document.title` 을 브랜드 공통값으로
 * 덮어써 제목 없는 화면이 전부 홈과 같은 제목으로 떨어졌다(GA4 페이지 제목 합산·검색 중복 문서 사고).
 *
 * ⚠️ Vue Query 프리패치(SSG SEO 핵심) — `useQuery` 는 setup 에서 data=undefined 로 시작하고 vite-ssg 의
 * `renderToString` 1패스는 응답을 기다리지 않는다. 프리패치가 없으면 공개 페이지 HTML 이 본문 데이터 없이
 * ("불러오는 중") 생성된다. 그래서 SSR 빌드 패스에서 **렌더할 라우트의 데이터만** 페이지와 같은 queryKey 로
 * 채우고(`src/ssg/prefetch.ts`), dehydrate 한 상태를 initialState 에 직렬화한다. 브라우저는 hydrate 로 복원해
 * 첫 useQuery 가 캐시 히트 → 서버 DOM 과 같은 DOM 으로 수화한 뒤, staleTime 이 지났으면 최신값으로 갱신한다.
 *
 * SSR 은 라우트마다 QueryClient 를 새로 만든다 — vite-ssg 가 라우트를 병렬 렌더하므로 공유 인스턴스면
 * 다른 페이지 데이터가 서로의 HTML 에 섞인다. 브라우저는 단일 인스턴스(`queryClient`) 그대로다.
 *
 * 프리렌더 대상 경로는 아래 `includedRoutes` export(vite-ssg 가 config 보다 우선)가 소유한다 — 동적 경로를
 * 앱의 API 함수로 펼치기 위해 server entry 에 둔다(`src/ssg/routes.ts`).
 */
export const createApp = ViteSSG(
  App,
  { routes, scrollBehavior },
  async ({ app, router, isClient, initialState, routePath }) => {
    app.use(createPinia());

    let client = queryClient;
    if (import.meta.env.SSR) {
      // 빌드 타임: 이 라우트가 렌더하는 공개 데이터만 페이지와 동일 queryKey 로 선로딩.
      client = createQueryClient();
      const { prefetchRoute } = await import('#/ssg/prefetch');
      // routePath 가 없는 호출은 vite-ssg 가 라우트 목록만 얻으려는 1회성 생성이다 — 조회하지 않는다.
      if (routePath) await prefetchRoute(routePath, client);
      // dehydrate 한 캐시를 initialState 에 실어 생성 HTML 에 직렬화한다(클라 hydrate 의 입력).
      initialState.vueQuery = dehydrate(client);
    } else if (initialState.vueQuery) {
      // 브라우저: VueQueryPlugin 등록 전에 캐시를 복원해 첫 useQuery 가 즉시 히트하도록 한다.
      hydrate(queryClient, initialState.vueQuery);
    }

    app.use(VueQueryPlugin, { queryClient: client });

    // 인증 가드·기본 타이틀은 브라우저에서만 — 빌드 타임(SSR)엔 API·document 접근이 없어야 한다.
    if (isClient) {
      registerAuthGuard(router);
      // GA4(마케팅 유입 분석) — 측정 ID 미설정(dev)이면 스크립트를 주입하지 않아 통째로 no-op 이 된다.
      // 첫 afterEach 가 발화하기 전에 초기화해야 랜딩 페이지뷰가 유실되지 않는다.
      initAnalytics();
      registerAnalytics(router);
      // 무료 개방 모드 설정(029) 선로딩 — 가드가 /checkout 차단을 판정하기 전에 값이 있어야 한다.
      // 가드도 자체적으로 await 하지만(경합 안전), 여기서 미리 걸어 첫 내비게이션 지연을 줄인다.
      // 실패는 http.ts 가 토스트로 알리고 유료(기본) 동작을 유지 — 부팅을 막지 않도록 여기서 흡수한다.
      useCommerceStore()
        .loadConfig()
        .catch(() => {});
    }
  },
);

/**
 * 프리렌더 대상 경로 — vite-ssg 가 server entry 의 이 export 를 config `includedRoutes` 보다 우선 사용한다.
 * 색인 정책은 `config/seoRoutes.ts`, 동적 경로 펼침은 `ssg/routes.ts` 가 소유한다.
 */
export async function includedRoutes(): Promise<string[]> {
  const { resolvePrerenderRoutes } = await import('#/ssg/routes');
  return resolvePrerenderRoutes();
}
